import { useCallback, useEffect, useRef, type Dispatch, type SetStateAction } from 'react';
import L from 'leaflet';
import * as turf from '@turf/turf';
import type { GridParams } from 'contexts/GridContext';
import { MAX_ALLOWED_GRID_AREA, MIN_ANALYSIS_AREA } from 'constants/grid';
import { isWithinSwitzerland } from 'services/isWithinSwitzerland';
import { extractLargestLv95RingCoordinates, simplifyLv95PolygonCoordinates } from 'utils/geometryHelper.js';
import { useLegalBorderParcelSelection } from './useLegalBorderParcelSelection';
import {
  getLayerMap,
  useParcelSelectionOutsideClick,
  type BorderType,
  type Lv95Coordinate,
  type SetGridParams,
  type TurfPolygon,
} from './cadastralShared';
import { buildTurfPolygonFromLv95, getCombinedAnalysisAreasCenter } from './cadastralGeometry';
import type { AddCenterMarker } from './AnalysisAreaCenterMarkerSync';

interface ParcelSelectionOptions {
  isLegalBorderParcelMode: boolean;
  setIsLegalBorderParcelMode: Dispatch<SetStateAction<boolean>>;
  activeAnalysisAreaIndex: number;
  analysisAreas: Lv95Coordinate[][];
  gridParams: GridParams;
  legalBorderRef: { current: L.Polygon | null };
  buildableBorderRef: { current: L.Polygon | null };
  analysisAreaRef: { current: L.Polygon | null };
  setIsDrawHintVisible: Dispatch<SetStateAction<boolean>>;
  setDrawHintText: Dispatch<SetStateAction<string>>;
  setShowEditableCoordinatesArea: Dispatch<SetStateAction<boolean>>;
  setCoordinates: Dispatch<SetStateAction<Lv95Coordinate[]>>;
  setEditingPolygonType: Dispatch<SetStateAction<BorderType | null>>;
  setIsGridAnalysisAreaValid: Dispatch<SetStateAction<boolean>>;
  setGridAnalysisAreaErrorMessage: Dispatch<SetStateAction<string>>;
  validateLegalBuildableContainment: (borderType: BorderType, polygon: TurfPolygon | null) => boolean;
  validateProjectRadiusRequirement: (borderType: BorderType, polygon: TurfPolygon | null) => boolean;
  validateAnalysisAreaSeparation: (
    borderType: BorderType,
    polygon: TurfPolygon | null,
    options: { ignoreIndex?: number }
  ) => boolean;
  syncNonAnalysisBorder: (borderType: Exclude<BorderType, 'analysisArea'>, coordinates: Lv95Coordinate[]) => void;
  syncAnalysisAreas: (
    areas: Lv95Coordinate[][],
    options: { activeIndex?: number; analysisAreaType?: string; recenter?: boolean }
  ) => void;
  addCenterMarker: AddCenterMarker;
  setGridParams: SetGridParams;
  notify: (message: string, type: 'error' | 'success') => void;
}

const useCadastralParcelSelection = ({
  isLegalBorderParcelMode,
  setIsLegalBorderParcelMode,
  activeAnalysisAreaIndex,
  analysisAreas,
  gridParams,
  legalBorderRef,
  buildableBorderRef,
  analysisAreaRef,
  setIsDrawHintVisible,
  setDrawHintText,
  setShowEditableCoordinatesArea,
  setCoordinates,
  setEditingPolygonType,
  setIsGridAnalysisAreaValid,
  setGridAnalysisAreaErrorMessage,
  validateLegalBuildableContainment,
  validateProjectRadiusRequirement,
  validateAnalysisAreaSeparation,
  syncNonAnalysisBorder,
  syncAnalysisAreas,
  addCenterMarker,
  setGridParams,
  notify,
}: ParcelSelectionOptions) => {
  const handleCadastralParcelSelected = (feature: GeoJSON.Feature, borderType: BorderType) => {
    const rawCoordinates = extractLargestLv95RingCoordinates(feature?.geometry);
    const coordinates = simplifyLv95PolygonCoordinates(rawCoordinates, { tolerance: 0.08 });
    if (coordinates.length < 3) {
      notify('Unable to use this cadastral parcel.', 'error');
      return false;
    }
    const polygon = buildTurfPolygonFromLv95(coordinates);
    if (!polygon) {
      notify('Unable to use this cadastral parcel.', 'error');
      return false;
    }
    const area = turf.area(polygon);
    if (area < MIN_ANALYSIS_AREA) {
      notify('The selected parcel is too small.', 'error');
      return false;
    }
    if (area > MAX_ALLOWED_GRID_AREA) {
      notify("The polygon area is greater than 10'000'000 m²!", 'error');
      return false;
    }
    if (turf.kinks(polygon).features.length !== 0) {
      notify('The polygon intersects itself!', 'error');
      return false;
    }
    if (
      !validateLegalBuildableContainment(borderType, polygon) ||
      !validateProjectRadiusRequirement(borderType, polygon) ||
      !validateAnalysisAreaSeparation(borderType, polygon, { ignoreIndex: activeAnalysisAreaIndex })
    ) {
      return false;
    }
    if (!isWithinSwitzerland(coordinates, gridParams.analysisAreaType)) {
      notify('Every polygon point must be inside Switzerland.', 'error');
      return false;
    }

    switch (borderType) {
      case 'legalBorder':
        syncNonAnalysisBorder('legalBorder', coordinates);
        break;
      case 'buildableBorder':
        syncNonAnalysisBorder('buildableBorder', coordinates);
        break;
      case 'analysisArea': {
        const nextAreas =
          analysisAreas.length === 0
            ? [coordinates]
            : analysisAreas.map((areaCoordinates, index) =>
                index === activeAnalysisAreaIndex ? coordinates : areaCoordinates
              );
        syncAnalysisAreas(nextAreas, {
          activeIndex: analysisAreas.length === 0 ? 0 : activeAnalysisAreaIndex,
          analysisAreaType: 'newAnalysisArea',
          recenter: true,
        });
        setIsGridAnalysisAreaValid(true);
        setGridAnalysisAreaErrorMessage('');
        const combinedCenter = getCombinedAnalysisAreasCenter(nextAreas);
        addCenterMarker(
          polygon,
          getLayerMap(analysisAreaRef.current) ||
            getLayerMap(legalBorderRef.current) ||
            getLayerMap(buildableBorderRef.current),
          setGridParams,
          combinedCenter ? [combinedCenter.x, combinedCenter.y] : null
        );
        break;
      }
      default:
        return false;
    }

    setEditingPolygonType(borderType);
    setCoordinates(coordinates);
    setShowEditableCoordinatesArea(true);
    notify('Area assigned from cadastral parcel.', 'success');
    return true;
  };

  const clearParcelModeUi = useCallback(() => {
    setIsLegalBorderParcelMode(false);
    setIsDrawHintVisible(false);
    setDrawHintText('');
  }, []);

  const applyLegalBorderFeature = useCallback(
    (feature: GeoJSON.Feature) => handleCadastralParcelSelected(feature, 'legalBorder') !== false,
    [handleCadastralParcelSelected]
  );

  const {
    selectedLegalBorderParcelFeatures,
    clearSelectedLegalBorderParcelFeatures,
    handleLegalBorderParcelSelected,
    handleSaveLegalBorderParcelSelection,
  } = useLegalBorderParcelSelection({
    applyLegalBorderFeature,
    showError: (message) => notify(message, 'error'),
    clearParcelModeUi,
  });

  const handleLegalBorderParcelModeInterruption = useCallback(() => {
    if (!isLegalBorderParcelMode) return;
    if (selectedLegalBorderParcelFeatures.length > 0) {
      handleSaveLegalBorderParcelSelection();
      return;
    }
    clearSelectedLegalBorderParcelFeatures();
    clearParcelModeUi();
  }, [
    clearParcelModeUi,
    clearSelectedLegalBorderParcelFeatures,
    handleSaveLegalBorderParcelSelection,
    isLegalBorderParcelMode,
    selectedLegalBorderParcelFeatures.length,
  ]);

  const previousAnalysisAreaIndexRef = useRef(activeAnalysisAreaIndex);
  useEffect(() => {
    if (previousAnalysisAreaIndexRef.current === activeAnalysisAreaIndex) return;
    previousAnalysisAreaIndexRef.current = activeAnalysisAreaIndex;
    handleLegalBorderParcelModeInterruption();
  }, [activeAnalysisAreaIndex, handleLegalBorderParcelModeInterruption]);

  useParcelSelectionOutsideClick(isLegalBorderParcelMode, handleLegalBorderParcelModeInterruption);

  return {
    selectedLegalBorderParcelFeatures,
    clearSelectedLegalBorderParcelFeatures,
    handleLegalBorderParcelSelected,
    handleSaveLegalBorderParcelSelection,
    handleLegalBorderParcelModeInterruption,
  };
};

export default useCadastralParcelSelection;
